import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  addWishlistProduct,
  getWishlist as getServerWishlist,
  removeWishlistProduct,
} from "../core/services/wishlistService";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext(null);
const WISHLIST_KEY = "sugamStoreGuestWishlist";
const WISHLIST_PRODUCTS_KEY = "sugamStoreGuestWishlistProducts";

const readJson = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const productIdOf = (item) => {
  const product = item?.product || item;
  return String(product?._id || product?.id || "");
};

export function WishlistProvider({ children }) {
  const { token } = useAuth();
  const [wishlistIds, setWishlistIds] = useState(() => readJson(WISHLIST_KEY, []));
  const [guestWishlist, setGuestWishlist] = useState(() =>
    readJson(WISHLIST_PRODUCTS_KEY, []),
  );

  useEffect(() => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlistIds));
  }, [wishlistIds]);

  useEffect(() => {
    localStorage.setItem(WISHLIST_PRODUCTS_KEY, JSON.stringify(guestWishlist));
  }, [guestWishlist]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const syncWishlist = async () => {
      const guestSaved = readJson(WISHLIST_PRODUCTS_KEY, []);
      for (const product of guestSaved) {
        const id = productIdOf(product);
        if (!id) continue;
        try {
          await addWishlistProduct(id);
        } catch {
          // Existing or unavailable saved items do not block wishlist sync.
        }
      }
      if (guestSaved.length) localStorage.removeItem(WISHLIST_PRODUCTS_KEY);
      const wishlist = await getServerWishlist().catch(() => null);
      if (cancelled || !wishlist?.wishlist?.products) return;
      setWishlistIds(
        wishlist.wishlist.products.map((item) => String(item?._id || item)),
      );
      setGuestWishlist(
        wishlist.wishlist.products.filter(
          (item) => item && typeof item === "object",
        ),
      );
    };
    void syncWishlist();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const toggleWishlist = useCallback(async (product) => {
    const productId = productIdOf(product);
    const exists = wishlistIds.includes(productId);
    if (token) {
      if (exists) await removeWishlistProduct(productId);
      else await addWishlistProduct(productId);
    }
    setWishlistIds((current) =>
      exists ? current.filter((id) => id !== productId) : [...current, productId],
    );
    setGuestWishlist((current) =>
      exists
        ? current.filter((entry) => productIdOf(entry) !== productId)
        : token
          ? current
          : [
              ...current.filter((entry) => productIdOf(entry) !== productId),
              product,
            ],
    );
  }, [token, wishlistIds]);

  const value = useMemo(
    () => ({ wishlistIds, guestWishlist, toggleWishlist }),
    [wishlistIds, guestWishlist, toggleWishlist],
  );

  return (
    <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) throw new Error("useWishlist must be used inside WishlistProvider");
  return context;
}
